import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { RouteComponentProps } from "react-router";
import {
  dispatchLoginError,
  dispatchTenantOptions,
  loginEMRServer,
  passportCallback,
} from "store/actions/user-context.actions";
import { GlobalState } from "store/configure-store";
import {
  selectLicenseUrl,
  selectLoginError,
  selectLoginStatus,
  selectTenantOptions,
} from "store/reducers/user-context.reducer";
import {
  LOGIN_STATUS_ERROR,
  LOGIN_STATUS_LOGGING_IN,
  LOGIN_STATUS_NO_LICENCE,
  LOGIN_STATUS_PARSING_PASSPORT,
  LOGIN_STATUS_RETRIEVING,
  LOGIN_STATUS_START,
  LOGIN_STATUS_SUCCESS,
  LOGIN_STATUS_TENANT_OPTIONS,
  UserContextLoginStatus,
} from "store/types/user-context.types";
import { TenantOption } from "common/dto/TenantOption";
import { useTranslation } from "react-i18next";
import Alert from "@cc/react-components/Alert";
import Button from "@cc/react-components/Button";
import Card from "@cc/react-components/Card";
import Page from "@cc/react-components/Page";
import Navbar from "@cc/react-components/Navbar";
import ListGroup from "ui/ListGroup";
import LoadingScreen from "main/LoadingScreen";

export interface Props
  extends React.HTMLAttributes<HTMLElement>,
    RouteComponentProps {
  /**
   * Fired after parsing the url search parameters
   * @param idToken the Corilus Passport token
   * @param state the state retrieved from URL
   */
  passportCallback(idToken: string, state: any): void;

  /**
   * Fired when clicking one of the tenant options when multiple found
   * @param idToken the Corilus Passport token
   * @param tenant the selected tenant name
   */
  loginEMRServer(idToken: string, tenant: string): void;

  /**
   * Dispatch function for tenant options
   * @param tenantOptions the Array<TenantOptions>
   */
  dispatchTenantOptions(tenantOptions: Array<TenantOption>): void;

  /**
   * Status of the login flow, updated the text and elements to show
   */
  loginStatus: UserContextLoginStatus;
  /**
   * Error message to display
   */
  loginError: string;
  /**
   * Array of tenants to pick from
   */
  tenantOptions: Array<TenantOption>;
  /**
   * The url to buy a license
   */
  licenseUrl: string;
}

const LoginCallback = ({
  loginStatus,
  loginError,
  tenantOptions,
  licenseUrl,
  passportCallback,
  loginEMRServer,
  dispatchTenantOptions,
  ...props
}: Props) => {
  const [t] = useTranslation();
  const [idToken, setIdToken] = useState<string>("");
  useEffect(() => {
    const hash = new URLSearchParams(props.history.location.hash.substring(1));
    if (hash.get("error")) {
      dispatchLoginError(t("error.mfa"));
    } else {
      const idToken = hash.get("id_token") || "";
      let urlState = hash.get("state");
      const state = JSON.parse(urlState ? atob(urlState) : "{}");
      setIdToken(idToken);
      passportCallback(idToken, state);
    }
  }, [passportCallback, props.history.location.hash, t]);

  const translateLoginStatus = (loginStatus: UserContextLoginStatus) => {
    switch (loginStatus) {
      case LOGIN_STATUS_START:
        return t("login.statusStart");
      case LOGIN_STATUS_PARSING_PASSPORT:
        return t("login.statusParsing");
      case LOGIN_STATUS_RETRIEVING:
        return t("login.statusTenants");
      case LOGIN_STATUS_LOGGING_IN:
        return t("login.statusLoggingIn");
      case LOGIN_STATUS_SUCCESS:
        return t("login.statusSuccess");
      case LOGIN_STATUS_ERROR:
        return "";
      case LOGIN_STATUS_TENANT_OPTIONS:
        return t("login.statusTenantOptions");
    }
  };

  function renderLoginErrorButtons() {
    return (
      <div className="h-100 d-flex align-items-center justify-content-center">
        <div>
          <div>
            <Button onClick={() => props.history.replace("/")} size="lg">
              {t("login.home")}
            </Button>
          </div>
          {tenantOptions.length > 0 && (
            <div className="mt-4">
              <Button
                variant="primary"
                onClick={() => dispatchTenantOptions(tenantOptions)}
                size="lg"
              >
                {t("login.tenantOptions")}
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  function renderNoLicence() {
    return (
      <div className="h-100 d-flex align-items-center justify-content-center">
        <div>
          <div>
            <Button
              variant="primary"
              onClick={() => window.open(licenseUrl, "_blank")}
              size="lg"
            >
              {t("login.buyLicense")}
            </Button>
          </div>
          <div className="mt-4">
            <Button
              variant="primary"
              onClick={() => props.history.replace("/")}
              size="lg"
            >
              {t("login.home")}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  function renderTenantOptions() {
    return (
      <ListGroup>
        {tenantOptions.map((tenantOption, index) => (
          <ListGroup.ItemMobile
            key={index}
            onClick={() => loginEMRServer(idToken, tenantOptions[index].name)}
          >
            {tenantOption.name}
          </ListGroup.ItemMobile>
        ))}
      </ListGroup>
    );
  }

  function renderPageTitle() {
    return (
      <Card>
        <Card.Header>
          <Card.Title>{translateLoginStatus(loginStatus)}</Card.Title>
        </Card.Header>
      </Card>
    );
  }

  function renderLoginError() {
    return <Alert variant="danger">{loginError}</Alert>;
  }

  return (
    <>
      <Navbar>
        <Navbar.Brand application="Visit" />
      </Navbar>
      <Page>
        <Page.Body>
          {loginStatus === LOGIN_STATUS_TENANT_OPTIONS && renderPageTitle()}
          {loginStatus !== LOGIN_STATUS_TENANT_OPTIONS &&
            loginStatus !== LOGIN_STATUS_ERROR &&
            loginStatus !== LOGIN_STATUS_NO_LICENCE && (
              <LoadingScreen message={translateLoginStatus(loginStatus)} />
            )}
          {loginStatus === LOGIN_STATUS_TENANT_OPTIONS && renderTenantOptions()}
          {loginError && renderLoginError()}
          {loginStatus === LOGIN_STATUS_NO_LICENCE && renderNoLicence()}
          {loginStatus === LOGIN_STATUS_ERROR && renderLoginErrorButtons()}
        </Page.Body>
      </Page>
    </>
  );
};

const mapStateToProps = (state: GlobalState) => {
  return {
    loginStatus: selectLoginStatus(state),
    loginError: selectLoginError(state),
    tenantOptions: selectTenantOptions(state),
    licenseUrl: selectLicenseUrl(state),
  };
};

export default connect(mapStateToProps, {
  passportCallback,
  loginEMRServer,
  dispatchTenantOptions,
  dispatchLoginError,
})(LoginCallback);
